import { ReactNode, useRef, useEffect, forwardRef } from 'react';
import { IconDotsVertical } from '@tabler/icons-react';
import OutsideClickHandler from 'react-outside-click-handler';
import { ComponentBlockingAppLoader } from 'components/atoms/CommonAppComponents/AppLoader/AppLoader';
import {
    AppPopover,
    AppPopoverProps,
} from 'components/atoms/CommonAppComponents/AppPopover/AppPopover';
import { OpenAppOptionsButton, OpenAppOptionsButtonSize } from './AppOptionsButton.styled';

export type AppOptionsButtonProps = Pick<AppPopoverProps, 'placement'> & {
    optionButtons: ReactNode;
    isVisiblePopover: boolean;
    isOptionsLoading?: boolean;
    openOptionsButtonSize?: OpenAppOptionsButtonSize;
    onPopoverHide: () => void;
    onDotsButtonClick: () => void;
    accessibilityLabel?: string;
};

export const AppOptionsButton = forwardRef<HTMLButtonElement, AppOptionsButtonProps>(
    (
        {
            optionButtons,
            isVisiblePopover,
            isOptionsLoading,
            openOptionsButtonSize,
            placement = 'leftTop',
            onPopoverHide,
            onDotsButtonClick,
            accessibilityLabel = 'Otwórz menu opcji',
        },
        ref,
    ) => {
        const popoverRef = useRef<HTMLDivElement>(null);

        // Handle keyboard navigation
        const handleKeyDown = (event: React.KeyboardEvent) => {
            switch (event.key) {
                case 'Enter':
                case ' ':
                    event.preventDefault();
                    onDotsButtonClick();
                    break;
                case 'Escape':
                    if (isVisiblePopover) {
                        event.preventDefault();
                        onPopoverHide();
                    }
                    break;
                default:
                    break;
            }
        };

        // Handle keyboard navigation when popover is open
        useEffect(() => {
            if (isVisiblePopover) {
                const handleGlobalKeyDown = (event: KeyboardEvent) => {
                    if (event.key === 'Escape') {
                        event.preventDefault();
                        onPopoverHide();
                    }
                };

                document.addEventListener('keydown', handleGlobalKeyDown);
                return () => document.removeEventListener('keydown', handleGlobalKeyDown);
            }
            return undefined;
        }, [isVisiblePopover, onPopoverHide]);

        // Focus management
        useEffect(() => {
            if (isVisiblePopover && popoverRef.current) {
                // Focus the first interactive element in the popover
                setTimeout(() => {
                    const firstButton = popoverRef.current?.querySelector('button');
                    if (firstButton) {
                        (firstButton as HTMLElement).focus();
                    }
                }, 0);
            }
        }, [isVisiblePopover]);

        const handleOutsideClick = () => {
            if (isVisiblePopover) {
                onPopoverHide();
            }
        };

        const popoverContent = (
            <div ref={popoverRef} role="menu" aria-label="Menu opcji">
                {optionButtons}
                {isOptionsLoading && <ComponentBlockingAppLoader />}
            </div>
        );

        return (
            <OutsideClickHandler onOutsideClick={handleOutsideClick}>
                <AppPopover
                    open={isVisiblePopover}
                    placement={placement}
                    arrow={false}
                    content={popoverContent}
                >
                    <OpenAppOptionsButton
                        ref={ref}
                        icon={<IconDotsVertical size={20} />}
                        withoutBorderRadius
                        ghost
                        margin={0}
                        onClick={onDotsButtonClick}
                        onKeyDown={handleKeyDown}
                        size={openOptionsButtonSize}
                        aria-label={accessibilityLabel}
                        aria-expanded={isVisiblePopover}
                        aria-haspopup="menu"
                        role="button"
                        tabIndex={0}
                    />
                </AppPopover>
            </OutsideClickHandler>
        );
    },
);
